import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePersona } from '@/auth/usePersona';
import { RAIL_ITEMS } from '@routes/railNavConfig';
import type { RailItem, RailItemKey, RailItemViewModel } from '@/types/leftNavRail.types';

export interface UseRailVisibleItemsResult {
  readonly items: readonly RailItemViewModel[];
  readonly toggleGroup: (key: RailItemKey) => void;
}

function getModulePrefix(item: RailItem): string {
  if (item.children && item.children.length > 0) {
    const firstChild = item.children[0]!;
    const segments = firstChild.route.split('/').filter(Boolean);
    return segments.length > 0 ? `/${segments[0]}` : item.defaultRoute;
  }
  return item.defaultRoute;
}

function pathMatches(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

export function useRailVisibleItems(): UseRailVisibleItemsResult {
  const { persona } = usePersona();
  const location = useLocation();
  // Parent groups start expanded by default so their child routes are visible
  // without requiring the user to toggle each chevron.
  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<RailItemKey>>(
    () =>
      new Set(RAIL_ITEMS.filter((item) => (item.children?.length ?? 0) > 0).map((item) => item.key))
  );

  const toggleGroup = useCallback((key: RailItemKey) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const items = useMemo<readonly RailItemViewModel[]>(() => {
    const path = location.pathname;
    return RAIL_ITEMS.filter(
      (item) => !item.hidden && (!item.allowedPersonas || item.allowedPersonas.includes(persona))
    ).map((item) => {
      const modulePrefix = getModulePrefix(item);
      const isActive =
        pathMatches(path, modulePrefix) ||
        (item.extraActivePrefixes?.some((prefix) => pathMatches(path, prefix)) ?? false);

      const visibleChildren = item.children ?? [];
      const activeChild = visibleChildren.find((child) => pathMatches(path, child.route));
      const activeChildKey = activeChild?.key ?? null;

      const isGroupExpanded =
        visibleChildren.length > 0 && (activeChildKey !== null || expandedGroups.has(item.key));

      return {
        item,
        isActive,
        visibleChildren,
        activeChildKey,
        isGroupExpanded,
      };
    });
  }, [persona, location.pathname, expandedGroups]);

  return { items, toggleGroup };
}
